import { apiService } from '../config/api';
import { FitbitExportDateBasis, FitbitExportFormat } from './fitbitExtractionService';
import { CollectionSourceId, collectionSources } from '../config/collectionSources';

export type DailyParticipant = { participantId: string; routeKeys: string[] | null };
export type DailyExtractionConfiguration = { scheduleId?: string; version: number; enabled: boolean; time: string; timeZone: string;
  name: string; format: FitbitExportFormat; dateBasis?: FitbitExportDateBasis; participants: DailyParticipant[];
  email: { enabled: boolean; recipients: string[] }; nextRunAt: string | null; updatedAt?: string };
export type MeasurementPreset = { id: string; name: string; routeKeys: string[] | null; fromTemplate?: boolean };
export type DailyExtractionSetup = { configuration: DailyExtractionConfiguration | null; defaultEmail: string; emailAvailable: boolean;
  presets: MeasurementPreset[]; lastRun: { dataDate: string; state: string; completed: number; failed: number; delivery: string; attachment: string | null } | null };
export type DailyExtractionSummary = DailyExtractionConfiguration & { scheduleId: string; createdBy: string };
export type DailyExtractionEdit = { scheduleId: string; initialStep?: 0 | 1; initialParticipantId?: string; emailInitiallyEnabled?: boolean };
export type DailyExtractionPayload = Omit<DailyExtractionConfiguration, 'scheduleId' | 'version' | 'nextRunAt' | 'updatedAt'> & { expectedVersion: number };

export function createCollectionDailyService(source: Exclude<CollectionSourceId, 'healthkit'> = 'fitbit') {
  const root = collectionSources[source].apiBase;
  const base = (studyId: string) => `${root}/studies/${encodeURIComponent(studyId)}/daily-extraction`;

  function getDailyExtraction(studyId: string, scheduleId?: string): Promise<DailyExtractionSetup> {
    return apiService.get(`${base(studyId)}${scheduleId === 'new' ? '/new' : scheduleId ? `/schedules/${encodeURIComponent(scheduleId)}` : ''}`);
  }

  function listDailyExtractions(studyId: string, after?: string): Promise<{ schedules: DailyExtractionSummary[]; nextCursor: string | null }> {
    return apiService.get(`${base(studyId)}/schedules${after ? `?after=${encodeURIComponent(after)}` : ''}`);
  }

  function createDailyExtraction(studyId: string, payload: DailyExtractionPayload & { requestId: string }): Promise<{ configuration: DailyExtractionConfiguration }> {
    return apiService.post(`${base(studyId)}/schedules`, payload);
  }

  function saveDailyExtraction(studyId: string, payload: DailyExtractionPayload, scheduleId?: string): Promise<{ configuration: DailyExtractionConfiguration }> {
    return apiService.post(`${base(studyId)}${scheduleId ? `/schedules/${encodeURIComponent(scheduleId)}` : ''}`, payload);
  }

  function controlDailyExtraction(studyId: string, scheduleId: string, expectedVersion: number, action: 'pause' | 'resume' | 'email_off'): Promise<{ configuration: DailyExtractionConfiguration }> {
    return apiService.post(`${base(studyId)}/schedules/${encodeURIComponent(scheduleId)}/actions`, { expectedVersion, action });
  }

  function saveMeasurementPreset(studyId: string, name: string, routeKeys: string[] | null): Promise<{ preset: MeasurementPreset }> {
    return apiService.post(`${base(studyId)}/presets`, { name, routeKeys });
  }
  return { getDailyExtraction, listDailyExtractions, createDailyExtraction, saveDailyExtraction, controlDailyExtraction, saveMeasurementPreset };
}

export const { getDailyExtraction, listDailyExtractions, createDailyExtraction, saveDailyExtraction, controlDailyExtraction, saveMeasurementPreset } = createCollectionDailyService();
